import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Fertigungsauftrag } from '../types';
import AktivitaetenBlock from './AktivitaetenBlock';
import './FertigungsauftragList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const FertigungsauftragList: React.FC = () => {
  const [fas, setFas] = useState<Fertigungsauftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);

  const loadFas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      const url = `${API_URL}/api/fertigungsauftraege${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setFas(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Fertigungsaufträge:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadFas();
  }, [loadFas]);

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Geplant': 'fa-status-geplant',
      'Gestartet': 'fa-status-gestartet',
      'Unterbrochen': 'fa-status-unterbrochen',
      'Fertig': 'fa-status-fertig',
    };
    return map[status] || '';
  };

  const getAgStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Offen': 'ag-status-offen',
      'In Bearbeitung': 'ag-status-aktiv',
      'Abgeschlossen': 'ag-status-fertig',
    };
    return map[status] || '';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="fa-list-container">
      <div className="list-header">
        <h2>Fertigungsaufträge</h2>
        <div className="header-actions">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            <option value="Geplant">Geplant</option>
            <option value="Gestartet">Gestartet</option>
            <option value="Unterbrochen">Unterbrochen</option>
            <option value="Fertig">Fertig</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : fas.length === 0 ? (
        <div className="empty-state">
          <p>Keine Fertigungsaufträge vorhanden</p>
          <p className="empty-hint">Fertigungsaufträge werden vom Produktions-Agent erstellt (z.B. via Chat).</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="fa-table">
            <thead>
              <tr>
                <th></th>
                <th>FA-Nr.</th>
                <th>Auftrag</th>
                <th>Kunde</th>
                <th>Status</th>
                <th>Fortschritt</th>
                <th>Geplant</th>
                <th>Erstellt</th>
              </tr>
            </thead>
            <tbody>
              {fas.map((fa) => (
                <React.Fragment key={fa.fa_id}>
                  <tr
                    className={`fa-row ${expandedId === fa.fa_id ? 'expanded' : ''}`}
                    onClick={() => toggleExpand(fa.fa_id)}
                  >
                    <td className="expand-cell">
                      {(fa.stueckliste?.length || fa.arbeitsgaenge?.length)
                        ? (expandedId === fa.fa_id ? '\u25BC' : '\u25B6')
                        : ''}
                    </td>
                    <td className="fa-nr">{fa.fa_nr}</td>
                    <td>{fa.auftrag_nr || '-'}</td>
                    <td>{fa.kunde_name || '-'}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(fa.status)}`}>
                        {fa.status}
                      </span>
                    </td>
                    <td>
                      <div className="fortschritt-bar">
                        <div
                          className="fortschritt-fill"
                          style={{ width: `${fa.fortschritt_prozent}%` }}
                        />
                        <span className="fortschritt-text">
                          {fa.arbeitsgaenge_fertig}/{fa.arbeitsgaenge_gesamt}
                        </span>
                      </div>
                    </td>
                    <td className="datum-range">
                      {formatDate(fa.start_geplant)} - {formatDate(fa.ende_geplant)}
                    </td>
                    <td>
                      {formatDate(fa.erstellt_am)}{' '}
                      <button
                        type="button"
                        className="k-btn k-btn--ghost k-btn--sm"
                        style={{ marginLeft: 6 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAkteId(akteId === fa.fa_id ? null : fa.fa_id);
                        }}
                      >
                        {akteId === fa.fa_id ? 'Akte schließen' : 'Akte'}
                      </button>
                    </td>
                  </tr>
                  {akteId === fa.fa_id && (
                    <tr className="detail-row">
                      <td colSpan={8}>
                        <div className="fa-detail-container">
                          <AktivitaetenBlock entitaetTyp="fertigungsauftrag" entitaetId={fa.fa_id} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {expandedId === fa.fa_id && (
                    <tr className="detail-row">
                      <td colSpan={8}>
                        <div className="fa-detail-container">
                          {/* Arbeitsgänge */}
                          {fa.arbeitsgaenge && fa.arbeitsgaenge.length > 0 && (
                            <div className="detail-section">
                              <h4>Arbeitsgänge</h4>
                              <table className="detail-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Arbeitsgang</th>
                                    <th>Maschine</th>
                                    <th>Soll (h)</th>
                                    <th>Ist (h)</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fa.arbeitsgaenge.map((ag) => (
                                    <tr key={ag.gang_id}>
                                      <td>{ag.reihenfolge}</td>
                                      <td>{ag.bezeichnung}</td>
                                      <td>{ag.maschine || '-'}</td>
                                      <td>{ag.dauer_soll ?? '-'}</td>
                                      <td>{ag.dauer_ist ?? '-'}</td>
                                      <td>
                                        <span className={`ag-badge ${getAgStatusClass(ag.status)}`}>
                                          {ag.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Stückliste */}
                          {fa.stueckliste && fa.stueckliste.length > 0 && (
                            <div className="detail-section">
                              <h4>Stückliste (Material)</h4>
                              <table className="detail-table">
                                <thead>
                                  <tr>
                                    <th>Artikel</th>
                                    <th>Soll</th>
                                    <th>Ist</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fa.stueckliste.map((pos) => (
                                    <tr key={pos.position_id}>
                                      <td>{pos.artikel_bezeichnung || `#${pos.artikel_id}`}</td>
                                      <td>{pos.menge_soll} {pos.artikel_einheit || ''}</td>
                                      <td>{pos.menge_ist} {pos.artikel_einheit || ''}</td>
                                      <td>
                                        <span className={`ag-badge ${pos.menge_ist >= pos.menge_soll ? 'ag-status-fertig' : 'ag-status-offen'}`}>
                                          {pos.menge_ist >= pos.menge_soll ? 'Komplett' : 'Offen'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {fa.notizen && (
                            <div className="fa-notizen">
                              <strong>Notizen:</strong> {fa.notizen}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FertigungsauftragList;
